import type { LocationTopic, WikiTopic } from '../types/detailsCard.types';

function buildMobileWikiUrl(langCode: string, titleForLang: string): string {
  const encodedTitle = encodeURIComponent(String(titleForLang || '').replace(/ /g, '_'));
  return `https://${langCode}.m.wikipedia.org/wiki/${encodedTitle}?minervanightmode=1`;
}

async function fetchArticleLangLinks(
  title: string,
  sourceArticleLang: string,
  signal: AbortSignal,
): Promise<Array<{ lang: string; title: string }>> {
  const base = `https://${sourceArticleLang}.wikipedia.org/w/api.php`;
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'langlinks',
    lllimit: 'max',
    format: 'json',
    origin: '*',
  });

  const resp = await fetch(`${base}?${params.toString()}`, { signal });
  if (!resp.ok) throw new Error(`Wikipedia API error (langlinks): ${resp.status}`);

  const data = await resp.json();
  const pages = data.query?.pages;
  if (!pages) return [];

  const pageId = Object.keys(pages)[0];
  if (!pageId || !pages[pageId].langlinks) return [];

  return pages[pageId].langlinks.map((link: any) => ({
    lang: link.lang,
    title: link['*'],
  }));
}

async function fetchWikidataSitelinks(qid: string, signal: AbortSignal): Promise<Record<string, any>> {
  const params = new URLSearchParams({
    action: 'wbgetentities',
    format: 'json',
    ids: qid,
    props: 'sitelinks',
    origin: '*',
  });

  const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Wikidata API error (sitelinks): ${resp.status}`);

  const data = await resp.json();
  const entity = data?.entities?.[qid];
  return entity?.sitelinks || {};
}

export async function resolveWikipediaUrlForTopic(params: {
  topic: WikiTopic | LocationTopic;
  viewportWikiLanguage: string;
  userPreferredWikiLangCode?: string;
  signal: AbortSignal;
}): Promise<{ url: string | null; error: string | null }> {
  const { topic, viewportWikiLanguage, userPreferredWikiLangCode, signal } = params;
  const userPrefLang = userPreferredWikiLangCode || 'en';

  if (topic.type === 'location') {
    const qid = String(topic.wikidataTag || '').trim();
    if (!qid) {
      return { url: null, error: 'Wikipedia page not available for this location' };
    }

    const sitelinks = await fetchWikidataSitelinks(qid, signal);
    const preferredOrder = Array.from(new Set([userPrefLang, 'en', viewportWikiLanguage]));

    for (const langCode of preferredOrder) {
      const link = sitelinks?.[`${langCode}wiki`];
      if (link?.title) {
        return { url: buildMobileWikiUrl(langCode, link.title), error: null };
      }
    }

    const anyWikipediaEntry = Object.entries(sitelinks || {}).find(([siteKey, value]) => {
      return siteKey.endsWith('wiki') && !siteKey.endsWith('wikivoyage') && (value as any)?.title;
    });

    if (anyWikipediaEntry) {
      const [siteKey, value] = anyWikipediaEntry;
      const langCode = siteKey.replace(/wiki$/, '') || 'en';
      const title = (value as any)?.title || '';
      return { url: buildMobileWikiUrl(langCode, title), error: null };
    }

    return { url: null, error: 'Wikipedia page not available for this location' };
  }

  const articleTitle = String(topic.name || '').trim();
  if (!articleTitle) {
    return { url: null, error: null };
  }

  const sourceLang = topic.sourceWikiLang || viewportWikiLanguage;

  if (userPrefLang === sourceLang) {
    return { url: buildMobileWikiUrl(userPrefLang, articleTitle), error: null };
  }

  try {
    const links = await fetchArticleLangLinks(articleTitle, sourceLang, signal);

    const pref = links.find((l) => l.lang === userPrefLang);
    if (pref) return { url: buildMobileWikiUrl(userPrefLang, pref.title), error: null };

    const english = links.find((l) => l.lang === 'en');
    if (english) return { url: buildMobileWikiUrl('en', english.title), error: null };

    return { url: buildMobileWikiUrl(sourceLang, articleTitle), error: null };
  } catch {
    return { url: buildMobileWikiUrl(sourceLang, articleTitle), error: null };
  }
}
